/**
 * Graphes, mode « Chemins et chaînes » : on coche les arêtes dans la matrice
 * d'adjacence M ; (Mᵏ)ᵢⱼ compte les chemins (graphe orienté) ou les chaînes
 * (graphe non orienté) de longueur k de i à j, et on les
 * voit un par un sur le graphe.
 */

import { useState } from 'react';
import { MathText, cn } from '@jeraisonne/ui';
import { Segmented, Stepper, ToggleChip } from '../../ui/controls';
import { frenchSpaces, superscript } from '../../ui/format';
import { Live, MatrixGrid } from '../matrix-calc/matrix-ui';
import { GraphFigure, type GEdge } from './graph-figure';
import { NAMES, adjPow, pathText, paths, type Adj } from './markov';

const START: Record<3 | 4, Adj> = {
  3: [
    [0, 1, 1],
    [1, 0, 1],
    [1, 1, 0],
  ],
  4: [
    [0, 1, 1, 1],
    [1, 0, 1, 0],
    [1, 1, 0, 1],
    [1, 0, 1, 0],
  ],
};

const SHOW = 12;

export function PathsMode() {
  const [size, setSize] = useState<3 | 4>(4);
  const [adj, setAdj] = useState<Adj>(START[4]);
  const [oriented, setOriented] = useState(false);
  const [k, setK] = useState(2);
  const [cell, setCell] = useState<[number, number]>([0, 0]);
  const [pick, setPick] = useState<number | null>(null);

  const n = adj.length;
  const Mk = adjPow(adj, k);
  const [i, j] = cell;
  const found = paths(adj, i, j, k, SHOW);
  const chosen = pick !== null ? found.list[pick] : undefined;
  const onPath = (a: number, b: number) =>
    !!chosen &&
    chosen.some(
      (v, t) =>
        t > 0 &&
        ((chosen[t - 1] === a && v === b) || (!oriented && chosen[t - 1] === b && v === a)),
    );
  const edges: GEdge[] = [];
  adj.forEach((row, a) =>
    row.forEach((e, b) => {
      if (e) edges.push({ from: a, to: b, strong: onPath(a, b) });
    }),
  );
  const names = NAMES.slice(0, n);
  const reset = () => setPick(null);
  // vocabulaire du programme : chemin dans un graphe orienté, chaîne sinon
  const word = oriented ? 'chemin' : 'chaîne';
  const Word = oriented ? 'Chemin' : 'Chaîne';

  const toggle = (a: number, b: number) => {
    setAdj((m) =>
      m.map((r, x) =>
        r.map((v, y) => ((x === a && y === b) || (!oriented && x === b && y === a) ? 1 - v : v)),
      ),
    );
    reset();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          ariaLabel="Nombre de sommets"
          value={String(size) as '3' | '4'}
          options={[
            { value: '3', label: '3 sommets' },
            { value: '4', label: '4 sommets' },
          ]}
          onChange={(v) => {
            const s = Number(v) as 3 | 4;
            setSize(s);
            setAdj(START[s]);
            setCell([0, 0]);
            reset();
          }}
        />
        <ToggleChip
          on={oriented}
          onClick={() => {
            // repasser en non orienté rend la matrice symétrique
            if (oriented) setAdj((m) => m.map((r, x) => r.map((v, y) => (v || m[y]![x] ? 1 : 0))));
            setOriented((o) => !o);
            reset();
          }}
        >
          Orienté
        </ToggleChip>
      </div>

      <GraphFigure
        n={n}
        edges={edges}
        oriented={oriented}
        label={`Graphe ${oriented ? 'orienté ' : ''}à ${n} sommets${chosen ? `, ${word} ${pathText(chosen)}` : ''}`}
      />

      <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
        <MatrixGrid
          name="M"
          labels={names}
          values={adj.map((r) => r.map(String))}
          onCell={toggle}
          disabled={(a, b) => a === b}
          cellLabel={(a, b) =>
            a === b
              ? `${NAMES[a]} vers ${NAMES[b]} : pas de boucle`
              : `${oriented ? 'Arc' : 'Arête'} ${NAMES[a]}${oriented ? ' → ' : '–'}${NAMES[b]} : ${adj[a]![b] ? 'oui' : 'non'}`
          }
        />
      </div>
      <p className="text-ink-600 text-sm">
        {frenchSpaces(
          oriented
            ? 'Touche une case pour ajouter ou enlever l’arc de la ligne vers la colonne.'
            : 'Touche une case pour ajouter ou enlever l’arête : M reste symétrique.',
        )}
      </p>

      <div className="max-w-56">
        <Stepper
          label="longueur k"
          value={k}
          min={1}
          max={6}
          onChange={(v) => {
            setK(v);
            reset();
          }}
        />
      </div>

      <MatrixGrid
        name={`M${superscript(k)}`}
        labels={names}
        values={Mk.map((r) => r.map(String))}
        selected={cell}
        onCell={(a, b) => {
          setCell([a, b]);
          reset();
        }}
        cellLabel={(a, b) =>
          `${Word}s de longueur ${k} de ${NAMES[a]} à ${NAMES[b]} : ${Mk[a]![b]}`
        }
      />

      <section
        className="border-ink-100 space-y-2 rounded-2xl border px-3 py-2"
        data-testid="gr-paths"
      >
        <Live
          text={`${found.total} ${word}${found.total > 1 ? 's' : ''} de longueur ${k} de ${NAMES[i]} à ${NAMES[j]}`}
        />
        <MathText className="text-ink-900 block">
          {`$(M^{${k}})_{${NAMES[i]}${NAMES[j]}} = ${found.total}$`}
        </MathText>
        <p className="text-ink-700 text-sm">
          {frenchSpaces(
            found.total === 0
              ? `Aucun${oriented ? '' : 'e'} ${word} de longueur ${k} de ${NAMES[i]} à ${NAMES[j]}.`
              : `${found.total} ${word}${found.total > 1 ? 's' : ''} de longueur ${k} de ${NAMES[i]} à ${NAMES[j]}${found.total > 1 ? ` : touche-en ${oriented ? 'un' : 'une'} pour ${oriented ? 'le' : 'la'} voir.` : ' :'}`,
          )}
        </p>
        {found.list.length > 0 && (
          <ul className="flex flex-wrap gap-2">
            {found.list.map((p, t) => (
              <li key={t}>
                <button
                  type="button"
                  aria-pressed={pick === t}
                  onClick={() => setPick(pick === t ? null : t)}
                  className={cn(
                    'min-h-11 rounded-xl border px-3 text-sm font-semibold tabular-nums',
                    pick === t
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-ink-200 text-ink-700 hover:bg-ink-50',
                  )}
                >
                  {pathText(p)}
                </button>
              </li>
            ))}
          </ul>
        )}
        {found.total > found.list.length && (
          <p className="text-ink-600 text-sm">
            … et {found.total - found.list.length} autre
            {found.total - found.list.length > 1 ? 's' : ''}.
          </p>
        )}
        <p className="text-ink-600 text-sm">
          {frenchSpaces(
            `Pourquoi ? ${oriented ? 'Un chemin' : 'Une chaîne'} de longueur k de i à j, c’est ${oriented ? 'un chemin' : 'une chaîne'} de longueur k − 1 de i vers un sommet t, puis ${oriented ? 'l’arc' : 'l’arête'} de t à j : c’est exactement le calcul ligne × colonne de Mᵏ⁻¹ × M.`,
          )}
        </p>
      </section>
    </div>
  );
}
